import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent } from "@/shared/ui/card";
import { Alert, AlertDescription } from "@/shared/ui/alert";
import { AlertCircle, Trash2 } from "lucide-react";
import { CoverUpload } from "@/features/packs/pages/CoverUpload";
import { PresetsList } from "@/features/packs/pages/PresetsList";
import { FormFields } from "@/features/packs/pages/FormFields";
import { DeletePackModal } from "@/features/packs/pages/DeletePackModal";
import { DeletePresetModal } from "@/features/presets/pages/DeletePresetModal";
import { usePackForm } from "@/features/packs/hooks/usePackForm";
import { useCustomQuery } from "@/shared/hooks/useCustomQuery";
import { useCustomMutation } from "@/shared/hooks/useCustomMutation";
import PackService from "@/features/packs/services/packService";
import { FormFooter } from "@/shared/widgets/form-actions";
import { SimpleButton } from "@/shared/widgets/feedback";
import { CreatorWithPercentage } from "@/features/packs/model/PackForm";
import { CheckIcon, XIcon } from "lucide-react";
import { CustomSwitch } from "@/shared/widgets/switch";
import { PageContent } from "@/shared/widgets/layout/PageContent";
import { packFormSchema, type PackFormData } from "@/shared/lib/validationSchemas";
import { useZodValidation } from "@/shared/hooks/useZodValidation";
import { Button } from "@/shared/ui/button";
import { toast } from "@/shared/hooks/use-toast";
import { useAuth } from "@/features/auth/model/AuthContext";
import { Permission } from "@/shared/types/roles";
import { useTranslation } from "@/shared/i18n";

export default function PackForm() {
  const { t } = useTranslation('packs');
  const { id } = useParams();
  const isEditing = !!id;
  const { data: packData } = useCustomQuery({
    serviceInstance: PackService.Instance,
    selectAction: (service) => service.getPackById,
    parameter: { id: id! },
    options: { enabled: isEditing },
  });
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(Permission.PACKS_EDIT);

  const [creatorsData, setCreatorsData] = useState<CreatorWithPercentage[]>([]);
  const [formFieldsValues, setFormFieldsValues] = useState<{ selectedCreator: string; selectedTags: string[] }>({ 
    selectedCreator: "",
    selectedTags: []
  });
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeletePresetModalOpen, setIsDeletePresetModalOpen] = useState(false);
  const [presetToDelete, setPresetToDelete] = useState<{ id: string; name: string } | null>(null);

  // Validação com Zod
  const validation = useZodValidation<PackFormData>({
    schema: packFormSchema,
    initialData: packData
  });

  // Mutation para deletar pack
  const deletePackMutation = useCustomMutation({
    serviceInstance: PackService.Instance,
    selectAction: (service) => service.deletePack,
  });

  // Prepare form data for validation
  const getFormData = () => {
    const form = document.querySelector('form');
    if (!form) return {
      name: "",
      description: "",
      creator: "",
      cover: "",
      published: false,
      creators: [],
      presets: []
    };

    const formData = new FormData(form);
    return {
      name: formData.get("name")?.toString() || "",
      description: formData.get("description")?.toString() || "",
      creator: formFieldsValues.selectedCreator || "",
      cover: coverPreview || "", // Use current coverPreview
      published,
      creators: creatorsData,
      presets: presets || []
    };
  };

  const {
    coverPreview,
    presets,
    submitting,
    formErrors,
    published,
    setPublished,
    handleCoverChange,
    handleSubmit: submitPackForm,
    handleAddPreset,
    handleRemovePreset: removePresetFromList,
    handleEditPreset,
    handleRemoveCover,
    createPackMutation,
    updatePackMutation,
    removePresetMutation,
  } = usePackForm({
    ...packData,
    packsCreators: creatorsData
  }, getFormData, formFieldsValues.selectedTags);

  // Função para deletar preset com confirmação
  const handleRemovePreset = async (presetId: string) => {
    try {
      await removePresetMutation.mutateAsync({ id: presetId });
      removePresetFromList(presetId);
      toast({
        title: t('presetDeleted'),
        description: t('presetDeletedDescription'),
      });
    } catch (error) {
      toast({
        title: t('errorDeletingPreset'),
        description: error instanceof Error ? error.message : t('errorUnexpected'),
        variant: "destructive",
      });
    }
  };

  // Handle form submission with validation
  const handleSubmit = async () => {
    if (!canEdit) return;
    // Validar formulário antes de submeter
    const data = getFormData();
    const isValid = validation.validate(data);

    if (!isValid) {
      toast({
        title: t('requiredFields'),
        description: t('fixErrors'),
        variant: "destructive",
      });
      return;
    }

    // Se válido, submeter o formulário
    submitPackForm(undefined);
  };

  // Função para deletar o pack
  const handleDeletePack = async () => {
    if (!canEdit) return;
    if (!id) return;

    try {
      await deletePackMutation.mutateAsync({ id });
      toast({
        title: t('packDeleted'),
        description: t('packDeletedDescription'),
      });
      navigate(-1);
    } catch (error) {
      toast({
        title: t('errorDeletingPack'),
        description: error instanceof Error ? error.message : t('errorUnexpected'),
        variant: "destructive",
      });
    } finally {
      setIsDeleteModalOpen(false);
    }
  };

  return (
    <PageContent title={isEditing ? t('editPack') : t('newPack')} items={[
      {
        label: t('home'),
        href: "/",
      },
      {
        label: isEditing ? t('editPack') : t('newPack'),
        href: `/packs/${id}`,
      },
    ]}>

      <Card className="bg-white/[0.03] border-white/[0.06]">
        <CardContent className="pt-6">
          {/* Alert de validação */}
          {!validation.isValid && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t('validationErrors', { count: Object.keys(validation.errors).length })}
              </AlertDescription>
            </Alert>
          )}

          <form className="space-y-8">

            <div className="flex flex-col md:flex-row gap-6">
              <CoverUpload
                coverPreview={coverPreview}
                onCoverChange={handleCoverChange}
                onRemoveCover={handleRemoveCover}
                error={!!validation.errors.cover}
                errorMessage={validation.errors.cover}
                title={t('cover')}
                onClearError={() => validation.clearFieldError("cover")}
                disabled={!canEdit}
              />

              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <CustomSwitch
                    checked={published}
                    onChange={(value) => {
                      if (!canEdit) return;
                      setPublished(value);
                    }}
                    id="published"
                    label={published ? t('publishedLabel') : t('draft')}
                    descriptionOn={t('publishedDescription')}
                    descriptionOff={t('draftDescription')}
                    iconOn={<CheckIcon className="w-12 h-12 text-green-500" />}
                    iconOff={<XIcon className="w-12 h-12 text-red-500" />}
                    className="bg-white/10 w-80"
                    disabled={!canEdit}
                  />

                </div>

              </div>
            </div>

            <FormFields
              defaultValues={packData}
              errors={validation.errors}
              onCreatorsChange={setCreatorsData}
              clearFieldError={validation.clearFieldError}
              onValuesChange={setFormFieldsValues}
              readOnly={!canEdit}
            />

            <PresetsList
              presets={presets}
              onAddPreset={handleAddPreset}
              onRemovePreset={(preset) => {
                setPresetToDelete({ id: preset.id, name: preset.name });
                setIsDeletePresetModalOpen(true);
              }}
              onEditPreset={handleEditPreset}
              canAddPreset={!!packData?.id}
              validationErrors={validation.errors}
              readOnly={!canEdit}
            />

            <FormFooter
              isSuccess={createPackMutation.isSuccess || updatePackMutation.isSuccess}
            >
              <div className="flex gap-3 justify-between w-full">
                {isEditing && canEdit ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsDeleteModalOpen(true)}
                    disabled={submitting || deletePackMutation.isPending}
                    className="bg-transparent border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300 hover:border-red-500/70"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t('deletePack')}
                  </Button>
                ):<div/>}
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(-1)}
                    className="bg-transparent border-[#333333] text-white hover:bg-[#222222] hover:text-white/60"
                    disabled={submitting}
                  >
                    {t('cancel')}
                  </Button>
                  {canEdit && (
                    <SimpleButton
                      onClick={handleSubmit}
                      isLoading={submitting}
                      isSuccess={createPackMutation.isSuccess || updatePackMutation.isSuccess}
                      loadingText={t('saving')}
                      successText={isEditing ? t('packUpdated') : t('packCreated')}
                      successDuration={3000}
                    >
                      {isEditing ? t('updatePack') : t('createPack')}
                    </SimpleButton>
                  )}
                </div>
              </div>
            </FormFooter>
          </form>
        </CardContent>
      </Card>

      {/* Modal de confirmação para deletar pack */}
      <DeletePackModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeletePack}
        packName={packData?.name}
        isLoading={deletePackMutation.isPending}
      />

      {/* Modal de confirmação para deletar preset */}
      <DeletePresetModal
        isOpen={isDeletePresetModalOpen}
        onClose={() => {
          setIsDeletePresetModalOpen(false);
          setPresetToDelete(null);
        }}
        onConfirm={async () => {
          if (presetToDelete) {
            await handleRemovePreset(presetToDelete.id);
            setIsDeletePresetModalOpen(false);
            setPresetToDelete(null);
          }
        }}
        name={presetToDelete?.name}
        isLoading={removePresetMutation.isPending}
      />
    </PageContent>
  );
}
